import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

const AboutPremium = () => {
  const seoData = {
    title: "About Livneu - Sri Lanka's Premier VTuber Agency",
    description:
      "Learn about Livneu's mission to empower virtual stars through professional management, cutting-edge technology, and global reach. Discover our journey, values, and vision.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/about",
    keywords:
      "Livneu about, VTuber agency Sri Lanka, LEVELSTAIR, virtual talent management, VTuber industry, virtual entertainment company",
  };

  const companyValues = [
    {
      icon: "star-outline",
      title: "Excellence",
      description:
        "We set the highest standards for virtual entertainment and talent development.",
    },
    {
      icon: "people-outline",
      title: "Community",
      description:
        "Building lasting relationships between creators and their global audiences.",
    },
    {
      icon: "rocket-outline",
      title: "Innovation",
      description:
        "Pioneering new technologies and methodologies in virtual content creation.",
    },
    {
      icon: "heart-outline",
      title: "Authenticity",
      description:
        "Helping creators express their true selves through virtual personas.",
    },
    {
      icon: "globe-outline",
      title: "Global Vision",
      description: "Connecting Sri Lankan talent with audiences worldwide.",
    },
    {
      icon: "trending-up-outline",
      title: "Growth",
      description:
        "Continuously evolving to provide the best opportunities for our talent.",
    },
  ];

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-about">
          {/* Kawaii floating elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-pastel-purple/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-pastel-blue/15 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-pastel-mint/20 rounded-full blur-2xl animate-pulse"></div>
          </div>
          
          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-primary mb-6 leading-tight kawaii-glow">
                About{" "}
                <span className="text-text-accent">
                  Livneu
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body">
                Sri Lanka's first professional VTuber agency, pioneering the
                future of virtual entertainment and empowering creators to reach
                global audiences.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-20 px-6 bg-bg-primary">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Mission */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-button rounded-2xl flex items-center justify-center shadow-kawaii">
                    <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h2 className="text-4xl font-title text-text-primary">
                    Our Mission
                  </h2>
                </div>

                <p className="text-lg text-text-secondary leading-relaxed mb-6 font-body">
                  To empower the next generation of virtual creators by
                  providing world-class management, cutting-edge technology, and
                  unparalleled support. We bridge the gap between Sri Lankan
                  talent and global audiences, creating opportunities for
                  digital stardom.
                </p>

                <div className="bg-gradient-card p-6 border-l-4 border-pastel-purple rounded-2xl shadow-soft">
                  <p className="text-text-secondary italic font-accent">
                    "Every virtual star deserves a stage. We're building that
                    stage for Sri Lanka."
                  </p>
                </div>
              </motion.div>

              {/* Vision */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center shadow-kawaii">
                    <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <h2 className="text-4xl font-title text-text-primary">
                    Our Vision
                  </h2>
                </div>

                <p className="text-lg text-text-secondary leading-relaxed mb-6 font-body">
                  To establish Livneu as the leading virtual
                  entertainment agency in South Asia, setting new standards for
                  VTuber management and becoming the go-to platform for virtual
                  talent development in the region.
                </p>

                <div className="bg-gradient-card p-6 border-l-4 border-pastel-blue rounded-2xl shadow-soft">
                  <p className="text-text-secondary italic font-accent">
                    "Building Sri Lanka's answer to the virtual entertainment
                    landscape, one virtual star at a time."
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Company Values */}
        <section className="py-20 px-6 bg-gradient-soft">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-title text-text-primary mb-6">
                Our Core Values
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto font-body">
                The principles that guide everything we do at Livneu
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {companyValues.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-card-background p-8 rounded-3xl shadow-kawaii hover:shadow-hover transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-button rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>

                  <h3 className="text-xl font-accent font-medium text-text-primary mb-4">
                    {value.title}
                  </h3>

                  <p className="text-text-secondary leading-relaxed font-body">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Timeline 
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                Our Journey
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
                From vision to reality - the Livneu story
              </p>
            </motion.div>

             <div className="max-w-4xl mx-auto">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  className={`flex items-center gap-8 mb-12 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}
                >
                  <div className="flex-1">
                    <div className={`bg-gradient-to-br ${index % 2 === 0 ? 'from-purple-50 to-blue-50 border-purple-400' : 'from-purple-50 to-blue-50 border-purple-500'} p-6 rounded-2xl border-l-4`}>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className={`w-16 h-16 bg-gradient-to-br ${index % 2 === 0 ? 'from-purple-400 to-blue-500' : 'from-purple-500 to-blue-600'} rounded-full flex items-center justify-center text-black font-bold text-sm`} style={{ fontFamily: "'Inter', sans-serif" }}>
                      {milestone.year}
                    </div>
                    {index < milestones.length - 1 && (
                      <div className="w-1 h-16 bg-gradient-to-b from-gray-300 to-transparent mt-2"></div>
                    )}
                  </div>
                  
                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div> 
          </div>
        </section>*/}

        {/* Team Stats 
        <section className="py-20 px-6 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                By the Numbers
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Our growing impact in the virtual entertainment industry
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {teamStats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  className="text-center bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10"
                >
                  <div
                    className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent mb-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {stat.number}
                  </div>
                  <h3
                    className="text-xl font-bold text-black mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {stat.label}
                  </h3>
                  <p
                    className="text-gray-700"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {stat.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>*/}

        {/* Call to Action */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Ready to Join Our Story?
              </h2>
              <p
                className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Become part of Livneu's mission to revolutionize virtual
                entertainment and showcase Sri Lankan talent to the world.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <motion.a
                  href="/apply"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 20px 40px rgba(151, 36, 233 0.3)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="glass-button px-8 py-4 text-lg no-underline"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Apply as Talent
                </motion.a>

                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="glass-card px-8 py-4 text-black font-semibold text-lg rounded-full border-white/20 hover:border-purple-400/30 transition-all duration-300 no-underline"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Get in Touch
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default AboutPremium;
